import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
    LoginModel, ChangeTenantModel, ChangeTenantResults, LoginResults, authSliceModel, decodedToken, refreshTokenModel, userSetup, routePage, UserSettingsUpdateRequest,
    userSettings, TenantPdfData, InitialSetupCreateDataResponse, InitialSetupResponse, InitialSetupRequest,
    EmailNotificationSetting, DemoCorporationInfo
} from './login-interfaces';
import {
    GetUserNotificationPreferencesResponse, UpdateUserNotificationPreferencesRequest
} from '../../UserSettings/state/user-notification-preferences';
import {
    UpdateUserPrivacySettingsRequest, UserPrivacySettings
} from '../../UserSettings/state/user-privacy-settings';
import { RejectedValue, ErrorResponse } from 'shared/interfaces/core-interfaces';
import { ConvertDateToString, getCurrentDate, RememberMe, ZeitonAccessToken, ZeitonAcessTokenApp, ZeitonRefreshToken } from 'shared/utils/common';
import { AuthHeader } from 'services/authHeader';
import jwtDecode from 'jwt-decode';
import i18n from 'i18n/config';
import { resetFilters } from '../../../ProfitCostCenters/ResourcePlanning/state/resourcePlanningSlice';
import { API_BASE_URL as baseUri } from 'config/env';

export const LogInRequest = createAsyncThunk<LoginResults, LoginModel, { rejectValue: RejectedValue }>(
    'AuthentificationSlice/Login',
    async (user: LoginModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/auth/login', {
            method: "GET",
            headers: {
                Authorization: "Basic " + btoa(user.email + ":" + user.password),
            },
        });
        if (response.status === 200) {
            const logInResponse = await response.json() as LoginResults;
            localStorage.setItem('rememberMe', JSON.stringify(user.rememberMe));
            if (user.rememberMe) {
                localStorage.setItem('AuthTokenApp', logInResponse.accessTokenApp);
                localStorage.setItem('AuthToken', logInResponse.accessToken);
                localStorage.setItem('RefreshToken', logInResponse.refreshToken);
            }
            else {
                sessionStorage.setItem('AuthTokenApp', logInResponse.accessTokenApp);
                sessionStorage.setItem('AuthToken', logInResponse.accessToken);
                sessionStorage.setItem('RefreshToken', logInResponse.refreshToken);
            }
            /*  localStorage.setItem('rememberMe', JSON.stringify(user.rememberMe)); */

            return logInResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

//TODO
export const LoginWithSSO = createAsyncThunk<LoginResults, void, { rejectValue: RejectedValue }>(
    "AuthSlicer/LoginWithSSO",
    async (_, { rejectWithValue }) => {
        try {
            const accessToken = localStorage.getItem(ZeitonAccessToken);
            //TODO - This will call the zeiton backend, not the centralization
            const response = await fetch(baseUri + "api/auth/ssotoken", {
                headers: { Authorization: 'Bearer ' + accessToken, },
            });
            // The SSO endpoint can answer 202 (Accepted) as well as 200, so accept
            // any 2xx success instead of strictly 200.
            if (response.ok) {
                const logInResponse = await response.json() as LoginResults;
                localStorage.setItem(RememberMe, JSON.stringify(true));
                localStorage.setItem(ZeitonAccessToken, logInResponse.accessToken);
                localStorage.setItem(ZeitonRefreshToken, logInResponse.refreshToken);
                localStorage.setItem(ZeitonAcessTokenApp, logInResponse.accessTokenApp);
                return logInResponse;
            }
            else {
                const errorResponse = await response.json() as ErrorResponse;
                return rejectWithValue({ message: errorResponse.detail, status: response.status });
            }
        }
        catch (error: any) {
            const errorMessage = error.message || 'Error in application';
            return rejectWithValue({ message: errorMessage, status: 404 });
        }
    }
);

export const ChangeTenantRequest = createAsyncThunk<ChangeTenantResults, ChangeTenantModel, { rejectValue: RejectedValue }>(
    'AuthentificationSlice/ChangeTenant',
    async (changeTenant: ChangeTenantModel, { rejectWithValue, dispatch }) => {
        const response = await fetch(baseUri + 'api/auth/changetenant?tenantId=' + changeTenant.tenantId, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const changeTenantResponse = await response.json() as ChangeTenantResults;
            const rememberMe: boolean = JSON.parse(localStorage.getItem('rememberMe') as string);
            if (rememberMe) {
                localStorage.setItem('AuthTokenApp', changeTenantResponse.accessTokenApp);
            }
            else {
                sessionStorage.setItem('AuthTokenApp', changeTenantResponse.accessTokenApp);
            }
            dispatch(resetFilters());
            return changeTenantResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const RefreshToken = createAsyncThunk<LoginResults, refreshTokenModel, { rejectValue: RejectedValue }>
    ('AuthentificationSlice/RefreshToken',
        async (tokens: refreshTokenModel, { rejectWithValue }) => {
            try {
                const response = await fetch(baseUri + 'api/auth/RefreshToken', {
                    method: "POST",
                    headers: AuthHeader(),
                    body: JSON.stringify(tokens)
                });
                if (response.status === 200) {
                    const refreshTokenResponse = await response.json() as LoginResults;
                    var rememberMe = JSON.parse(localStorage.getItem('rememberMe') as string) != null ? JSON.parse(localStorage.getItem('rememberMe') as string) : true;
                    localStorage.setItem('rememberMe', rememberMe);
                    if (rememberMe) {
                        localStorage.setItem('AuthTokenApp', refreshTokenResponse.accessTokenApp);
                        localStorage.setItem('AuthToken', refreshTokenResponse.accessToken);
                        localStorage.setItem('RefreshToken', refreshTokenResponse.refreshToken);
                    }
                    else {
                        sessionStorage.setItem('AuthTokenApp', refreshTokenResponse.accessTokenApp);
                        sessionStorage.setItem('AuthToken', refreshTokenResponse.accessToken);
                        sessionStorage.setItem('RefreshToken', refreshTokenResponse.refreshToken);
                    }

                    return refreshTokenResponse;
                }
                else {
                    const errorResponse = await response.json() as ErrorResponse;
                    return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
                }
            } catch (error: any) {
                throw new Error(error);
            }
        });

export const GetUserSetupData = createAsyncThunk<userSetup, void, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/GetUserSetupData',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/auth/getsetupdata', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as userSetup;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// UPDATE USER SETTINGS
export const UpdateUserSettings = createAsyncThunk<userSettings, UserSettingsUpdateRequest, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/UpdateUserSettings',
    async (userSettings: UserSettingsUpdateRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/employee/settings/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(userSettings)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as userSettings;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET USER SETTINGS
export const GetUserSettings = createAsyncThunk<userSettings, void, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/GetUserSettings',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/employee/settings/get', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as userSettings;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// USER NOTIFICATION PREFERENCES (WP6)
// The channel per notification type, per tenant. Replaces the one-boolean-per-type email opt-in that
// the settings modal used to write: the legacy flags are still read on the backend as the default for
// anyone who has never saved here, so nobody's existing opt-in was reset.
export const GetUserNotificationPreferences = createAsyncThunk<GetUserNotificationPreferencesResponse, string | undefined, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/GetUserNotificationPreferences',
    async (tenantId: string | undefined, { rejectWithValue }) => {

        // No tenant means "the one on my token", which is what the screen asks for when it opens.
        const query = tenantId ? `?tenantId=${tenantId}` : '';

        const response = await fetch(baseUri + 'api/employee/notification-preferences/get' + query, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetUserNotificationPreferencesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// Uses AuthHeader(true, true) to include the centralization ("Ack"/atc) token, as the email
// notification save it replaces did.
export const UpdateUserNotificationPreferences = createAsyncThunk<GetUserNotificationPreferencesResponse, UpdateUserNotificationPreferencesRequest, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/UpdateUserNotificationPreferences',
    async (request: UpdateUserNotificationPreferencesRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/employee/notification-preferences/update', {
            method: "PUT",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetUserNotificationPreferencesResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// USER PRIVACY SETTINGS (WP10)
// The outward direction: whether this user's birthday and name-day may be announced to colleagues.
// There is no GET of its own - the answers arrive on api/employee/settings/get with the rest of the
// user's settings, which the modal re-reads when it opens. Only the save is its own call, because the
// Privacy tab has its own Save button.
//
// Uses AuthHeader(true, true) to include the centralization ("Ack"/atc) token, like the other saves in
// this modal.
export const UpdateUserPrivacySettings = createAsyncThunk<UserPrivacySettings, UpdateUserPrivacySettingsRequest, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/UpdateUserPrivacySettings',
    async (request: UpdateUserPrivacySettingsRequest, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/employee/settings/privacy/update', {
            method: "PUT",
            headers: AuthHeader(true, true),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as UserPrivacySettings;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET TENANT SETTINGS
export const GetTenantPdfData = createAsyncThunk<TenantPdfData, string | undefined, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/GetTenantPdfData',
    async (id: string | undefined, { rejectWithValue }) => {
        const response = await fetch(`${baseUri}api/shared-endpoints/get-tenant-pdf-data${id ? `?tenantId=${id}` : ''}`, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as TenantPdfData;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const InitialSetupCreateData = createAsyncThunk<InitialSetupCreateDataResponse, void, { rejectValue: RejectedValue }>(
    '/Slice/InitialSetupSlice/CreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/corporations/setup-data', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as InitialSetupCreateDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const InitialSetupCreateFirstTenant = createAsyncThunk<InitialSetupResponse, InitialSetupRequest, { rejectValue: RejectedValue }>(
    '/Slice/InitialSetupSlice/CreateFirstTenant',
    async (data, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/tenant/create-first', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const responseSetup = await response.json() as InitialSetupResponse;
            localStorage.setItem('rememberMe', JSON.stringify(false));
            localStorage.setItem('AuthTokenApp', responseSetup.appAccessToken);
            sessionStorage.setItem('AuthTokenApp', responseSetup.appAccessToken);
            return responseSetup;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ChangeLicenseStatus = createAsyncThunk<{ accessToken: string }, string, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/ChangeLicenseStatus',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/auth/corporation/change-status?id=${id}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { accessToken: string };
            localStorage.setItem('AuthTokenApp', responseModel.accessToken);
            sessionStorage.setItem('AuthTokenApp', responseModel.accessToken);
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DemoLogin = createAsyncThunk<{ accessToken: string }, string, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/LoginToDemo',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/auth/corporation/select?id=${id}`, {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { accessToken: string };
            localStorage.setItem('AuthTokenApp', responseModel.accessToken);
            sessionStorage.setItem('AuthTokenApp', responseModel.accessToken);
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const DirectoryChoiceTokenUpdate = createAsyncThunk<{ accessToken: string }, string, { rejectValue: RejectedValue }>(
    'Slice/AuthentificationSlice/DirectoryChoiceTokenUpdate',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/auth/dir/choose`, {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify({ directoryId: id })
        });
        if (response.status === 200) {
            const responseModel = await response.json() as { accessToken: string };
            localStorage.setItem('AuthTokenApp', responseModel.accessToken);
            sessionStorage.setItem('AuthTokenApp', responseModel.accessToken);
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

const initialAuthValue: authSliceModel = {
    statusCode: '',
    showNotification: false,
    message: "",
    isLoading: false,
    isSwitchingTenant: false,
    error: '',
    hasLoginData: false,
    needToChooseCompany: false,
    tokenRefreshed: ConvertDateToString(getCurrentDate()),
    isTokenRefreshing: false,
    tenants: [],
    currentCulture: '',
    defaultTenantId: null,
    defaultTenantCurrency: "",
    email: '',
    password: '',
    rememberMe: false,
    logTenantId: null,
    logTenantName: '',
    temporaryLogTenantId: null,
    corporationName: '',
    corporationDefaultAppLanguage: 'en',
    userFullName: '',
    userAbv: '',
    userSettings: null,
    notificationPreferences: null,
    permissions: [],
    currentUrl: '',
    currentPage: '',
    currentRole: "",
    tenantPdfData: null,
    theme: null,
    setupRequired: false,
    nonDemoCorporationSetupAvailable: false,
    firstName: "",
    lastName: "",
    gender: "",
    userName: "",
    demoCorporation: null,
    corporationOptions: null,
    isCorporationEmailServisSetUp: false,
    showWhatsNew: false,
    termsAcceptanceRequired: false
}

export const AuthentificationSlice = createSlice({
    name: 'authentificationSlice',
    initialState: initialAuthValue,
    reducers: {
        logoutAuth: (state) => {
            state = initialAuthValue;
            const rememberMe = localStorage.getItem(RememberMe);
            localStorage.clear();
            if (rememberMe !== null) {
                localStorage.setItem(RememberMe, rememberMe);
            }
            sessionStorage.clear();
        },
        setRout: (state, action: PayloadAction<routePage>) => {
            state.currentPage = action.payload.currentPage;
            state.currentUrl = action.payload.currentUrl;
        },
        changeCulture: (state, action: PayloadAction<string>) => {
            state.currentCulture = action.payload;
        },
        resetInitialCorporationSettings: (state) => {
            state.setupRequired = false;
            state.nonDemoCorporationSetupAvailable = false;
            state.corporationOptions = null;
        },
        // The login response only fills demoCorporation when there is a single directory; with
        // several, the corporation picker sets it before opening the demo-license page.
        setDemoCorporation: (state, action: PayloadAction<DemoCorporationInfo>) => {
            state.demoCorporation = action.payload;
        },
        resetStatusCode: (state) => {
            state.showNotification = false;
            state.statusCode = '';
            state.message = '';
        },
        // Lets callers hold the app in its loading state across a hard reload (e.g. a tenant
        // switch), so the dashboard doesn't flash empty before its boot data arrives.
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        // Cleared once the user acknowledges the release, so the modal doesn't reopen on the
        // next remount. The server sends it fresh on each login anyway; this only keeps the
        // in-session (and persisted) copy honest.
        setShowWhatsNew: (state, action: PayloadAction<boolean>) => {
            state.showWhatsNew = action.payload;
        },
        // Cleared once the user accepts, so the blocking modal lets them through without
        // waiting for the next login payload.
        setTermsAcceptanceRequired: (state, action: PayloadAction<boolean>) => {
            state.termsAcceptanceRequired = action.payload;
        },
        // Full-screen loading overlay shown across a tenant switch + reload. Set true right before
        // the reload and cleared once boot data has settled, so the user never sees the app rebuild.
        setSwitchingTenant: (state, action: PayloadAction<boolean>) => {
            state.isSwitchingTenant = action.payload;
        }
    },
    extraReducers(builder) {
        //Login
        builder.addCase(LogInRequest.fulfilled, (state, action: PayloadAction<LoginResults>) => {
            state.corporationOptions = action.payload.corporationOptions ?? null;
            state.setupRequired = action.payload.setupRequired ?? false;
            state.nonDemoCorporationSetupAvailable = action.payload.nonDemoCorporationSetupAvailable ?? false;
            state.isLoading = false;
            state.hasLoginData = true;
            state.needToChooseCompany = false;
            state.demoCorporation = action.payload.demoCorporation ?? null;
            state.error = '';
            const decodedToken = jwtDecode(action.payload.accessTokenApp) as decodedToken;
            state.permissions = decodedToken.permission;
            state.firstName = decodedToken.firstname ?? "";
            state.lastName = decodedToken.lastname ?? "";
            state.gender = decodedToken.gender ?? "";
            state.userName = decodedToken.username ?? "";
        })
            .addCase(LogInRequest.pending, state => {
                state.isLoading = true;
            })
            .addCase(LogInRequest.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.hasLoginData = false;
                state.needToChooseCompany = false;
            })
        //LoginWithSSO
        builder.addCase(LoginWithSSO.fulfilled, (state, action: PayloadAction<LoginResults>) => {
            state.corporationOptions = action.payload.corporationOptions ?? null;
            state.setupRequired = action.payload.setupRequired ?? false;
            state.nonDemoCorporationSetupAvailable = action.payload.nonDemoCorporationSetupAvailable ?? false;
            state.isLoading = false;
            state.hasLoginData = true;
            state.needToChooseCompany = false;
            state.demoCorporation = action.payload.demoCorporation ?? null;
            state.error = '';
            const decodedToken = jwtDecode(action.payload.accessTokenApp) as decodedToken;
            state.permissions = decodedToken.permission;
            state.firstName = decodedToken.firstname ?? "";
            state.lastName = decodedToken.lastname ?? "";
            state.gender = decodedToken.gender ?? "";
            state.userName = decodedToken.username ?? "";
        })
            .addCase(LoginWithSSO.pending, state => {
                state.isLoading = true;
            })
            .addCase(LoginWithSSO.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.hasLoginData = false;
                state.needToChooseCompany = false;
            })
        //Change Tenant
        builder.addCase(ChangeTenantRequest.fulfilled, (state, action: PayloadAction<ChangeTenantResults>) => {
            state.isLoading = false;
            state.hasLoginData = true;
            state.needToChooseCompany = false;
            state.error = '';
            const decodedToken = jwtDecode(action.payload.accessTokenApp) as decodedToken;
            state.permissions = decodedToken.permission;
            state.logTenantId = action.payload.tenantId;
            state.logTenantName = action.payload.tenantName;
            state.defaultTenantCurrency = action.payload.defaultTenantCurrency;
            state.currentRole = action.payload.permissionRoleName;
        })
            .addCase(ChangeTenantRequest.pending, state => {
                state.isLoading = true;
            })
            .addCase(ChangeTenantRequest.rejected, (state, action) => {
                state.isLoading = false;
                state.logTenantId = state.temporaryLogTenantId;
                state.error = action.payload?.message ? action.payload?.message : 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.hasLoginData = false;
                state.needToChooseCompany = false;
            })
        //Refresh token
        builder.addCase(RefreshToken.fulfilled, (state, action: PayloadAction<LoginResults>) => {
            const decodedToken = jwtDecode(action.payload.accessTokenApp) as decodedToken;
            state.permissions = decodedToken.permission;
            state.isTokenRefreshing = false;
        })
            .addCase(RefreshToken.pending, state => {
                state.isTokenRefreshing = true;
            })
            .addCase(RefreshToken.rejected, (state, action) => {
                state = initialAuthValue;
                sessionStorage.clear();
                localStorage.clear();
                window.location.replace("/login");
            })
        //GetSetupData
        builder.addCase(GetUserSetupData.fulfilled, (state, action: PayloadAction<userSetup>) => {
            state.isLoading = false;
            state.error = '';
            state.logTenantId = action.payload.tenantId;
            state.logTenantName = action.payload.tenantName;
            state.userFullName = action.payload.employeeFullName;
            state.userAbv = action.payload.employeeAbv;
            state.tenants = action.payload.tenants;
            state.corporationName = action.payload.corporationName;
            state.corporationDefaultAppLanguage = action.payload.corporationDefaultAppLanguage;
            state.userSettings = action.payload.employeeSettings;
            state.currentRole = action.payload.currentRole;
            state.currentCulture = action.payload.employeeSettings?.culture!;
            state.defaultTenantCurrency = action.payload.defaultTenantCurrency;
            state.isCorporationEmailServisSetUp = action.payload.isCorporationEmailServisSetUp;
            state.showWhatsNew = action.payload.showWhatsNew;
            state.termsAcceptanceRequired = action.payload.termsAcceptanceRequired;
            i18n.changeLanguage(action.payload.employeeSettings?.culture!);
        })
            .addCase(GetUserSetupData.pending, state => {
                state.isLoading = true;
            })

        // None of the settings modal's calls touch isLoading: the dashboard swaps itself for a full-page
        // loader while it is set, so opening or saving the modal blanked the page behind it and remounted
        // every widget, which fetched all its data again. The modal shows its own loading state.
        // Update user settings
        builder.addCase(UpdateUserSettings.fulfilled, (state, action: PayloadAction<userSettings>) => {
            state.statusCode = "200";
            // The general-settings update response does not carry the email-notification
            // toggles, so keep whatever is already in state instead of wiping them.
            state.userSettings = {
                ...action.payload,
                emailNotifications: action.payload.emailNotifications ?? state.userSettings?.emailNotifications
            };
            state.currentCulture = action.payload.culture;
            state.showNotification = true;
            state.message = "User settings updated successfully.";
            // TODO: add move info if needed.
        })
            .addCase(UpdateUserSettings.rejected, (state, action) => {
                state.message = action.payload?.message ? action.payload?.message : 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.hasLoginData = false;
                state.needToChooseCompany = false;
            })


        // Get user settings
        builder.addCase(GetUserSettings.fulfilled, (state, action: PayloadAction<userSettings>) => {
            state.userSettings = action.payload;
            state.statusCode = "200";
            // The app language is left alone: the settings modal re-reads these on open, and switching
            // to the saved culture here undid a language picked from the menu for this session.
            // Login (GetUserSetupData) and saving the settings (UpdateUserSettings) set it.
            // TODO: add move info if needed.
        })
            .addCase(GetUserSettings.rejected, (state, action) => {
                state.message = action.payload?.message ? action.payload?.message : 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.hasLoginData = false;
                state.needToChooseCompany = false;
            })
        // User notification preferences (WP6)
        builder.addCase(GetUserNotificationPreferences.fulfilled, (state, action: PayloadAction<GetUserNotificationPreferencesResponse>) => {
            state.statusCode = "200";
            state.notificationPreferences = action.payload;
        })
            .addCase(GetUserNotificationPreferences.rejected, (state, action) => {
                state.message = action.payload?.message ? action.payload?.message : 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
        builder.addCase(UpdateUserNotificationPreferences.fulfilled, (state, action: PayloadAction<GetUserNotificationPreferencesResponse>) => {
            state.statusCode = "200";
            state.showNotification = true;
            state.message = "Notification preferences updated successfully.";
            // The PUT returns the resulting screen, so there is nothing to re-read.
            state.notificationPreferences = action.payload;
        })
            .addCase(UpdateUserNotificationPreferences.rejected, (state, action) => {
                state.message = action.payload?.message ? action.payload?.message : 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
        // User privacy settings (WP10)
        builder.addCase(UpdateUserPrivacySettings.fulfilled, (state, action: PayloadAction<UserPrivacySettings>) => {
            state.statusCode = "200";
            state.showNotification = true;
            state.message = "Privacy settings updated successfully.";
            // The PUT answers with what is now in force, so the settings already in state are updated
            // from it rather than re-read. Guarded because userSettings is null until login fills it.
            if (state.userSettings) {
                state.userSettings = { ...state.userSettings, privacy: action.payload };
            }
        })
            .addCase(UpdateUserPrivacySettings.rejected, (state, action) => {
                state.message = action.payload?.message ? action.payload?.message : 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
        // Get user settings
        builder
            .addCase(GetTenantPdfData.fulfilled, (state, action: PayloadAction<TenantPdfData>) => {
                state.tenantPdfData = action.payload;
                state.statusCode = "200";
                state.isLoading = false;
            })
            .addCase(GetTenantPdfData.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ? action.payload?.message : 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.hasLoginData = false;
                state.needToChooseCompany = false;
            })
            .addCase(GetTenantPdfData.pending, (state) => {
                state.isLoading = true;
            })
        //InitialSetupCreateFirstTenant
        builder
            .addCase(InitialSetupCreateFirstTenant.fulfilled, (state, action: PayloadAction<InitialSetupResponse>) => {
                const decodedToken = jwtDecode(action.payload.appAccessToken) as decodedToken;
                state.permissions = decodedToken.permission;
                state.setupRequired = false;
            })
            .addCase(InitialSetupCreateFirstTenant.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ? action.payload?.message : 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.hasLoginData = false;
                state.needToChooseCompany = false;
            })
            .addCase(InitialSetupCreateFirstTenant.pending, (state) => {
                state.isLoading = true;
            })
        //ChangeLicenseStatus
        builder
            .addCase(ChangeLicenseStatus.fulfilled, (state, action: PayloadAction<{ accessToken: string }>) => {
                state.statusCode = "200";
                state.isLoading = false;
                state.hasLoginData = true;
                // The corporation is chosen now: take the permissions from the session token, and drop
                // the choice so /login doesn't send the user back to the picker.
                const decodedToken = jwtDecode(action.payload.accessToken) as decodedToken;
                state.permissions = decodedToken.permission;
                state.corporationOptions = null;
                state.nonDemoCorporationSetupAvailable = false;
            })
            .addCase(ChangeLicenseStatus.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ? action.payload?.message : 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.hasLoginData = false;
                state.needToChooseCompany = false;
            })
            .addCase(ChangeLicenseStatus.pending, (state) => {
                state.isLoading = true;
            })
        //LoginToDemo
        builder
            .addCase(DemoLogin.fulfilled, (state, action: PayloadAction<{ accessToken: string }>) => {
                state.statusCode = "200";
                state.isLoading = false;
                state.hasLoginData = true;
                // Same as ChangeLicenseStatus: permissions from the session token, picker state dropped.
                const decodedToken = jwtDecode(action.payload.accessToken) as decodedToken;
                state.permissions = decodedToken.permission;
                state.corporationOptions = null;
                state.nonDemoCorporationSetupAvailable = false;
            })
            .addCase(DemoLogin.rejected, (state) => {
                // The pages that call this show the error themselves. Clearing hasLoginData here
                // bounced the user through /login and back, which remounted the page and lost both
                // the selection and the error; a notification raised here only popped up later,
                // once Main mounted.
                state.isLoading = false;
            })
            .addCase(DemoLogin.pending, (state) => {
                state.isLoading = true;
            })
    },
});

export const { logoutAuth, setRout, changeCulture, resetStatusCode, resetInitialCorporationSettings, setDemoCorporation, setLoading, setShowWhatsNew, setTermsAcceptanceRequired, setSwitchingTenant } = AuthentificationSlice.actions;
export default AuthentificationSlice.reducer;

